import { convertObservedJpy } from './exchangeRateCache.js'

const TRANSIENT_STATUS = new Set([401, 408, 425, 500, 502, 503, 504])
const PERMANENT_STATUS = new Set([400, 403, 404, 409, 422, 429])

export function scanHttpStatus(error) {
  return Number(error?.response?.status || error?.status || 0) || 0
}

export function classifyScanError(error) {
  const status = scanHttpStatus(error)
  if (PERMANENT_STATUS.has(status)) return 'failed'
  if (TRANSIENT_STATUS.has(status)) return 'retry'
  if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED') return 'retry'
  if (error?.name === 'TypeError') return 'retry'
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'retry'
  if (status >= 500) return 'retry'
  if (status >= 400) return 'failed'
  return 'retry'
}

export function describeScanSaveError(error, { saved = false, conversionPending = false } = {}) {
  if (saved && conversionPending) return 'Produit enregistré — conversion en attente'
  if (saved) return ''
  return 'Enregistrement impossible'
}

export function nextScanRetryAt(attempts, now = Date.now()) {
  const safeAttempts = Math.max(Number(attempts) || 1, 1)
  const delay = Math.min(30_000, 1000 * (2 ** Math.min(safeAttempts - 1, 5)))
  return new Date(now + delay).toISOString()
}

export function shouldRetryScanNow(entry, now = Date.now()) {
  if (!entry) return false
  if (entry.status === 'saved' || entry.status === 'failed' || entry.status === 'syncing') return false
  if (entry.nextRetryAt && Date.parse(entry.nextRetryAt) > now) return false
  return true
}

export function isConfirmedScanSave(created) {
  return Boolean(
    created?.id
    && !String(created.id).startsWith('pending-')
    && (created.cart_id || created.cartId),
  )
}

export function applyConversionToMeta(meta, conversion) {
  const current = meta && typeof meta === 'object' ? { ...meta } : {}
  const rate = Number(conversion?.rate)
  const observed = Number(current.observed_price_jpy)
  const converted = convertObservedJpy(observed, rate)
  const status = converted
    ? (conversion?.stale ? 'cached' : 'live')
    : 'pending'
  current.exchange_rate = converted ? rate : null
  current.exchange_rate_date = converted
    ? String(conversion?.date || conversion?.retrieved_at || '')
    : ''
  current.converted_price_eur = converted
  current.conversion_status = status
  current.conversion_stale = Boolean(conversion?.stale) && status !== 'pending'
  current.price = {
    ...(current.price || {}),
    converted,
    exchange_rate: converted ? rate : null,
    exchange_rate_date: current.exchange_rate_date,
  }
  return current
}

export function applyConversionToPayload(payload, conversion) {
  const next = {
    ...(payload || {}),
    item_meta: { ...(payload?.item_meta || {}) },
  }
  const currency = String(next.item_meta.observed_currency || next.item_meta.price?.currency || '').toUpperCase()
  if (currency === 'EUR' || next.item_meta.conversion_status === 'direct') {
    return next
  }
  if (conversion?.rate > 0) {
    next.item_meta = applyConversionToMeta(next.item_meta, conversion)
    next.price = next.item_meta.converted_price_eur || 0
    return next
  }
  next.price = 0
  next.item_meta.conversion_status = 'pending'
  next.item_meta.converted_price_eur = null
  next.item_meta.exchange_rate = null
  next.item_meta.conversion_stale = false
  next.item_meta.price = {
    ...(next.item_meta.price || {}),
    converted: null,
    exchange_rate: null,
  }
  return next
}

export function conversionIsPending(item) {
  const meta = item?.item_meta && typeof item.item_meta === 'object' ? item.item_meta : {}
  if (meta.conversion_status === 'direct' || String(meta.observed_currency || '').toUpperCase() === 'EUR') {
    return false
  }
  if (meta.conversion_status === 'pending') return true
  return !(Number(meta.converted_price_eur) > 0) && Number(meta.observed_price_jpy) > 0
}
